import { withZod } from "@rvf/zod";
import { MailCheck, RefreshCw } from "lucide-react";
import { href, redirect, useFetcher } from "react-router";
import { z } from "zod";
import { sendLoginLink } from "~/.server/lib/auth";
import { generateAuthUrl as generateGoogleAuthUrl } from "~/.server/lib/oauth-providers/google";
import { generateAuthUrl as generateMicrosoftAuthUrl } from "~/.server/lib/oauth-providers/microsoft";
import { getUserFromSession } from "~/.server/lib/session";
import Spinner from "~/components/Spinner";
import { Button, LinkButton } from "~/components/ui/Button";
import { InputField } from "~/components/ui/InputField";
import { AnonNavBar } from "~/components/ui/NavBar";
import type { Route } from "./+types/login";

export async function loader({ request }: Route.LoaderArgs) {
  // For login routes, we check if user is already logged in and redirect if so
  const user = await getUserFromSession(request);
  if (user) {
    return redirect(href("/"));
  }
  return {
    googleLoginUrl: generateGoogleAuthUrl("login"),
    microsoftLoginUrl: generateMicrosoftAuthUrl("login"),
  };
}

const loginSchema = withZod(
  z.object({
    email: z
      .string()
      .min(1, { message: "Email is required" })
      .email({ message: "Invalid email address" }),
  })
);

export async function action({ request }: Route.ActionArgs) {
  // For login routes, we don't use authenticateBuyer/Supplier since we're explicitly handling auth
  const formData = await request.formData();

  // Get redirect from URL params
  const url = new URL(request.url);
  const redirectTo = url.searchParams.get("redirectTo");

  // Check if the form is for resending the email
  const action = formData.get("action");
  if (action == "resend") {
    // No need for validation here since email is being passed from the previous form
    const email = formData.get("email");
    return { status: "resend", email };
  }

  const { data, error } = await loginSchema.validate(formData);
  if (error) return { error };

  // Send magic link to the email
  try {
    await sendLoginLink(data.email, redirectTo);
    return { status: "sent", email: data.email };
  } catch (error: any) {
    return { error };
  }
}

export default function Login({
  loaderData: { googleLoginUrl, microsoftLoginUrl },
}: Route.ComponentProps) {
  const fetcher = useFetcher();
  return (
    <div className="flex h-screen w-full">
      <AnonNavBar className="w-1/5" />
      {fetcher?.data?.status == "sent" ? (
        <main className="bg-dark-50 flex h-screen w-full flex-col items-center justify-center p-10">
          <div className="flex w-full flex-col items-center justify-center sm:w-lg md:w-md">
            <div className="bg-brand-primary bg-brand-primary shadow-brand-primary rounded-full p-6">
              <MailCheck className="text-dark-green size-[3rem]" />
            </div>
            <h1 className="h-lg mt-[2rem]">Check your email</h1>
            <span className="body-lg-400 text-dark-300 mt-[0.5rem] w-full text-center">
              Please check{" "}
              <span className="text-dark-900 body-lg-500">{` ${fetcher?.data?.email} `}</span>{" "}
              for login link
            </span>
            <fetcher.Form method="post" className="flex flex-col gap-[1.25rem]">
              <input type="hidden" name="email" value={fetcher?.data?.email} />
              <Button
                type="submit"
                variant="secondary"
                className="mt-[2rem]"
                name="action"
                value="resend"
              >
                <RefreshCw />
                Resend email
              </Button>
            </fetcher.Form>
          </div>
        </main>
      ) : (
        <main className="bg-dark-50 flex h-screen w-full flex-col items-center justify-center p-10">
          <div className="flex w-full flex-col items-center justify-center gap-[2rem] *:w-full sm:w-lg md:w-md">
            <div className="flex flex-col items-center justify-center gap-[2rem]">
              <img
                className="size-[2.5rem]"
                src="/icons/logo.svg#logo"
                alt="Logo"
              />
              <h1 className="h-lg">Welcome to Valdera</h1>
            </div>
            <div className="flex flex-col gap-[0.5rem]">
              <LinkButton
                to={googleLoginUrl}
                variant="secondary"
                className="justify-start"
              >
                <img
                  src="/icons/google.svg"
                  alt="Google Logo"
                  className="mr-2.5 w-5"
                />
                Continue with Google
              </LinkButton>
              <LinkButton
                to={microsoftLoginUrl}
                variant="secondary"
                className="justify-start"
              >
                <img
                  src="/icons/microsoft.svg"
                  alt="Microsoft Logo"
                  className="mr-2.5 w-5"
                />
                Continue with Microsoft
              </LinkButton>
            </div>
            <div className="body-sm-500 flex items-center justify-between gap-[1rem]">
              <div className="bg-dark-100 h-px w-full" />
              OR
              <div className="bg-dark-100 h-px w-full" />
            </div>
            <fetcher.Form method="post" className="flex flex-col gap-[1.25rem]">
              <InputField
                name="email"
                defaultValue={fetcher?.data?.email}
                placeholder="Work email"
                className="body-lg-400"
                errorMessage={
                  fetcher.data?.error?.fieldErrors?.email ??
                  fetcher.data?.error?.message
                }
              />

              <Button type="submit" variant="primary" size="lg">
                Continue
              </Button>
            </fetcher.Form>
            {fetcher.state != "idle" && <Spinner />}
          </div>
        </main>
      )}
    </div>
  );
}
